import React, { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { getUser, signOut } from "../data/authService";
import { getSupabaseClient } from "../lib/supabase";

export default function ProtectedRoute() {
    const [isValidating, setIsValidating] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const user = getUser();
    const userRole = user?.role ?? null;

    useEffect(() => {
        const validateSession = async () => {
            // First check if we have a local user token
            if (!user || userRole !== "admin") {
                setIsAuthenticated(false);
                setIsValidating(false);
                return;
            }

            // Validate the Supabase session is still valid by making a server call
            try {
                const supabase = getSupabaseClient();

                // getUser() makes an actual API call to validate the token with Supabase servers
                // Unlike getSession() which only returns cached data
                const { data: { user: supabaseUser }, error } = await supabase.auth.getUser();

                if (error || !supabaseUser) {
                    // Session is invalid or expired, clear local state (Navigate handles redirect)
                    await signOut(false);
                    setIsAuthenticated(false);
                    setIsValidating(false);
                    return;
                }

                // Session is valid
                setIsAuthenticated(true);
            } catch (err) {
                console.error("Error validating session:", err);
                // On error, clear local state to be safe (Navigate handles redirect)
                await signOut(false);
                setIsAuthenticated(false);
            } finally {
                setIsValidating(false);
            }
        };

        validateSession();
    }, [user, userRole]);

    // Show nothing while validating (prevents flash)
    if (isValidating) {
        return (
            <div className="flex h-screen items-center justify-center bg-[#FFFCEF]">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#304031] border-t-transparent" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return <Outlet />;
}
